import Core from './core';
import { flashBridgeFilterSchema } from './filters';
import { portfolioNarrative, reviewPriorities } from './operationalUi';
import type { DerivedFlash, FlashFilters, FlashOverview, FlashSnapshot } from './types';

export function buildFlashBridgeView(
  data: FlashSnapshot | undefined,
  result: DerivedFlash | null,
  filter: FlashFilters,
  ready: boolean,
  portfolio?: FlashOverview,
) {
  const individual = ['funnel', 'comparison'].includes(filter.tab);
  const campaign = individual && filter.campaign ? result?.selected : null;
  const aggregate = portfolio || (data && !individual ? Core.overview(data, filter) : null);
  const overview = aggregate
    ? {
        ...aggregate,
        filters: { ...aggregate.filters, campaign: '' },
        metrics: Object.fromEntries(
          Object.entries(aggregate.metrics).map(([id, row]) => {
            const { matchedCampaignIds, ...metric } = row;
            return [
              id,
              {
                ...metric,
                matchedCampaignIdsSample: matchedCampaignIds?.slice(0, 20),
                matchedCampaignIdsTruncated: (matchedCampaignIds?.length || 0) > 20,
              },
            ];
          }),
        ),
      }
    : null;
  const schema = flashBridgeFilterSchema(data?.campaigns || [], filter, result?.queue.map((r) => r.campaign) || []);
  return {
    title: individual ? 'Flash campaign benchmark' : 'Flash portfolio overview',
    ready,
    activeTab: filter.tab,
    viewScope: individual ? 'individual' : 'portfolio',
    overview: individual ? null : overview,
    filters: { ...filter, campaign: individual ? campaign?.id || filter.campaign : '' },
    filterSchema: schema,
    dataMode:
      'Dated reporting snapshot · ' +
      (filter.market
        ? 'Website market ' + filter.market + '; impression metrics withheld'
        : 'all source markets combined'),
    sourceCapabilities: data?.sourceCapabilities,
    scope: individual
      ? campaign
        ? result?.scope
        : { cutoff: result?.scope.cutoff, selectionRequired: true }
      : overview?.scope,
    dimensionMapping: { pcm: data?.sourceCapabilities?.pcm, territory: data?.sourceCapabilities?.territory },
    portfolioQueueSummary: {
      total: result?.queue.length || 0,
      shown: Math.min(25, result?.queue.length || 0),
      order: 'Below-band count descending, then lowest SD',
      completeFilteredCampaignIds: 'filterSchema.campaign.values (empty option excluded)',
    },
    portfolioQueue: result?.queue.slice(0, 25).map((r) => ({
      id: r.campaign.id,
      name: r.campaign.name,
      country: r.campaign.country,
      pcmName: r.campaign.pcmName,
      strategicTerritory: r.campaign.strategicTerritory,
      destination: r.campaign.destination,
      status: r.campaign.status,
      observedStart: r.campaign.observedStart,
      observedEnd: r.campaign.observationEnd,
      daysObserved: r.campaign.daysObserved,
      packagesPerDay: r.campaign.metrics.packages_per_day,
      below: r.below,
      scored: r.scored,
      minZ: r.minZ,
      weakestMetricId: r.weakestMetricId,
    })),
    selectedCampaign: campaign
      ? {
          id: campaign.id,
          name: campaign.name,
          country: campaign.country,
          destination: campaign.destination,
          owner: campaign.owner,
          pcmName: campaign.pcmName,
          strategicTerritory: campaign.strategicTerritory,
          campaignType: campaign.campaignType,
          startDate: campaign.startDate,
          endDate: campaign.endDate,
          observedStart: campaign.observedStart,
          observedEnd: campaign.observationEnd,
          daysObserved: campaign.daysObserved,
          totals: campaign.totals,
          coverage: campaign.coverage,
        }
      : null,
    benchmark:
      individual && campaign
        ? {
            selectedCampaignExcluded: true,
            metrics: result?.metrics,
            comparatorCount: result?.comparators.length || 0,
            comparatorIdsSample: result?.comparators.slice(0, 100).map((c) => c.id),
            comparatorIdsTruncated: (result?.comparators.length || 0) > 100,
            sameAge: result?.ageSensitivity,
          }
        : null,
    sourceFreshness: data
      ? {
          asOf: data.meta.asOf,
          generatedAt: data.meta.generatedAt,
          observationCutoff: data.meta.observationCutoff,
          factFreshness: data.meta.factFreshness,
          population: data.meta.population,
          dimensionTiming: data.meta.dimensionTiming,
        }
      : null,
    definitions: {
      missing: 'Unknown coverage is not zero demand.',
      pcm: data?.sourceCapabilities?.pcm?.definition,
      territory: data?.sourceCapabilities?.territory?.definition,
      margin: 'Sales margin before refunds, including item and surcharge contributions; AUD.',
      benchmark:
        'Descriptive distribution, not a causal conclusion or target. Minimum valid n is explicit. No automatic broader-peer fallback.',
      observation:
        'Absolute observation dates intersect selected and comparator facts. Campaign age is queue-only; first-N-launch-day evidence is separate.',
      market: data?.sourceCapabilities?.market?.reason,
    },
  };
}

/** Operational pages expose the same scoped readout and quartile positions as the visible UI. */
export function buildOperationalBridgeView(
  data: FlashSnapshot | undefined,
  result: import('./types').OperationalResult | null,
  filters: FlashFilters,
  ready: boolean,
) {
  const active = result?.filters || filters;
  const selected = active.view === 'campaign' ? result?.selected : null;
  const publicRow = (row: import('./types').OperationalRow) => ({
    id: row.id,
    name: row.name,
    country: row.country,
    destination: row.destination,
    strategicTerritory: row.territory,
    pcmName: row.pcm,
    ownerSalesforce: row.ownerSalesforce,
    campaignType: row.type,
    status: row.campaign.status,
    startDate: row.startDate,
    endDate: row.endDate,
    ageDays: row.ageDays,
    daysObserved: row.daysObserved,
    daysLeft: row.daysLeft,
    durationDays: row.durationDays,
    actual: row.actual,
    peerMedian: row.peerMedian,
    positions: row.positions,
    measureCounts: {
      total: Object.keys(row.positions).length,
      below: Object.values(row.positions).filter((position) => position === 'below').length,
      inLine: Object.values(row.positions).filter((position) => position === 'in_line').length,
      above: Object.values(row.positions).filter((position) => position === 'above').length,
      unscored: Object.values(row.positions).filter((position) => position === 'unscored').length,
    },
    peerCount: row.n,
    peerLevel: row.level,
    countryPeerFallback: row.level === 'country',
    tooEarly: row.tooEarly,
    missingSalesReview: row.zeroSale,
    weakestStep: row.weakestStep,
    gapToPeerPaceAUD: row.gap,
    marginToDateAUD: row.marginToDate,
    noComparisonReason: row.reason,
  });
  const trajectorySummary = selected?.trajectories
    ? Object.fromEntries(
        Object.entries(selected.trajectories).map(([measure, trajectory]) => {
          const latest = [...trajectory.points].reverse().find((point) => point.actual !== null);
          return [
            measure,
            {
              market: trajectory.market,
              latestRecorded: latest ? { day: latest.day, date: latest.date, cumulative: latest.actual } : null,
              lastPeerBand: trajectory.lastBandedPoint,
              originalPeerCount: trajectory.peerCount,
              minimumRemaining: trajectory.minimumRemaining,
              bandStartDay: trajectory.bandStartDay,
              bandEndDay: trajectory.bandEndDay,
              note: trajectory.note,
            },
          ];
        }),
      )
    : null;
  const rows = result?.rows || [];
  const listRows = result?.listRows || [];
  const attention = reviewPriorities(result?.attention || []);
  const weeklyView = active.view === 'portfolio' && active.historyView === 'weekly';
  const previewCount = weeklyView ? 5 : 15;
  const rollupMode = active.rollup === 'territory' || active.rollup === 'pcm' ? active.rollup : 'campaign';
  return {
    title: selected ? selected.name + ' · Flash performance' : 'Flash performance',
    ready: ready && !!result && !result.errors.length,
    activeView: active.view,
    viewScope: active.view === 'campaign' ? 'individual' : active.view === 'campaigns' ? 'campaign-list' : 'portfolio',
    filters: { ...active, campaign: active.view === 'campaign' ? active.campaign : '' },
    filterSchema: flashBridgeFilterSchema(
      data?.campaigns || [],
      active,
      rows.map((row) => row.campaign),
    ),
    dataMode: active.market
      ? `Dated reporting snapshot · website market ${active.market}; both impression measures withheld`
      : 'Dated reporting snapshot · all source markets combined',
    scope: result?.scope || null,
    verdict: result ? portfolioNarrative(result) : [],
    hero:
      active.view === 'portfolio' && active.historyView === 'previous' && result
        ? {
            comparison: 'previous-flash',
            metric: 'margin_per_day',
            ...result.previousRuns.summary.metrics.margin_per_day,
          }
        : result?.story.hero || null,
    counts: result?.counts || null,
    coverage: result?.coverage || null,
    headline: result?.headline || null,
    stageCounts: result?.stageCounts || null,
    stepRates: result?.stepRates || null,
    weeklyStory: result?.story || null,
    weeklySeries: result?.weekly || null,
    weeklyExclusionOptions: (data?.campaigns || [])
      .filter(
        (c) =>
          active.weeklyExclusion.split(',').includes(c.id) ||
          !active.search ||
          `${c.opportunityName || c.name} ${c.name}`.toLowerCase().includes(active.search.toLowerCase()),
      )
      .slice(0, 40)
      .map((c) => ({ id: c.id, name: c.opportunityName || c.name, startDate: c.startDate })),
    weeklyExclusionOptionsNote:
      'At most 40 labelled options are shown here. The assistant may edit the labelled IDs plus current selections in filterSchema.weeklyExclusion.values; the UI picker searches all campaigns; use the Search filter to locate other campaign names, then clear Search when applying the exclusion. Never infer IDs from names.',
    weeklySelection: {
      population: active.weeklyExclusion ? 'Flash with campaign exclusions' : 'All Flash',
      excludedCampaigns: result?.weekly.excludedCampaigns || [],
      appliesTo:
        'Weekly headline, charts and weekly CSV only; campaign queue, peer scores and Previous Flash are unchanged.',
    },
    previousFlash: result
      ? {
          active:
            active.view === 'campaign'
              ? active.campaignComparison === 'previous'
              : active.view === 'portfolio' && active.historyView === 'previous',
          scope: result.previousRuns.scope,
          summary: result.previousRuns.summary,
          selected: selected ? result.previousRuns.selected : null,
          rows: result.previousRuns.rows.slice(0, weeklyView ? 0 : 15).map((row) => ({
            currentId: row.currentId,
            name: row.name,
            priorId: row.priorId,
            priorName: row.priorName,
            available: row.available,
            reason: row.reason,
            matchBasis: row.matchBasis,
            matchedDays: row.matchedDays,
            truncated: row.truncated,
            currentWindow: row.current,
            priorWindow: row.prior,
            metrics: row.metrics,
          })),
          note: `${weeklyView ? 'Pair rows omitted while viewing the weekly trend' : 'First 15 current campaign pairs only'}; exact summary and selected pair retained. Previous Flash is a same-launch-age comparison with one previous run, separate from the destination/country peer benchmark.`,
        }
      : null,
    attention: attention.slice(0, previewCount).map(publicRow),
    attentionSummary: {
      total: attention.length,
      shown: Math.min(previewCount, attention.length),
      order:
        'Mature below-quartile campaigns only; gap to own peer median pace ascending. Capped at 28 remaining days.',
      note: `Top ${previewCount} rows only; exact aggregate counts, filters and roll-ups are retained.`,
    },
    campaignList: listRows.slice(0, previewCount).map(publicRow),
    campaignListSummary: { total: listRows.length, shown: Math.min(previewCount, listRows.length), sort: active.sort },
    rollup: {
      mode: rollupMode,
      rows: rollupMode === 'campaign' ? [] : result?.rollups[rollupMode] || [],
      note: 'All groups in the active roll-up are included. Behind peer pace follows the same mature below-quartile rule as the headline.',
    },
    selectedCampaign: selected ? publicRow(selected) : null,
    selectionRequired: active.view === 'campaign' && !selected,
    campaignVerdict: selected ? result?.verdict.campaign || [] : [],
    benchmark: selected
      ? {
          selectedCampaignExcluded: true,
          peerLevel: selected.level,
          peerCount: selected.n,
          metrics: Object.fromEntries(
            Object.entries(selected.metrics).map(([id, metric]) => [
              id,
              {
                value: metric.value,
                n: metric.n,
                q1: metric.q1,
                median: metric.median,
                q3: metric.q3,
                positionQuartile: metric.positionQuartile,
                reason: metric.reason,
              },
            ]),
          ),
          comparatorIdsSample: selected.peerCampaigns?.slice(0, 50).map((peer) => peer.id) || [],
          comparatorIdsTruncated: (selected.peerCampaigns?.length || 0) > 50,
        }
      : null,
    trajectorySummary,
    sourceFreshness: data
      ? {
          asOf: data.meta.asOf,
          generatedAt: data.meta.generatedAt,
          observationCutoff: data.meta.observationCutoff,
          factFreshness: data.meta.factFreshness,
          population: data.meta.population,
          dimensionTiming: data.meta.dimensionTiming,
          stale: data.stale,
        }
      : null,
    sourceCapabilities: data?.sourceCapabilities,
    definitions: {
      productScope:
        "Flash eligibility requires the bookable offer's product_bk = 'hotel' plus an eligible Flash campaign schedule. Resolve service offer IDs through dim_offer.offer_salesforce_bk; linked service product records take precedence over generic Salesforce offer records. A generic Salesforce record labelled hotel does not establish hotel eligibility when the bookable service product is tour.",
      position:
        'Well below peers is below the first quartile; in line is the middle half; well above is above the third quartile. Fewer than seven observation days is too early for a displayed band.',
      peerRule:
        'Mature completed 28–42 day campaigns of the same destination, country and type. With fewer than five destination peers with sales records, use same-country/type peers and explicitly label country peers. Active dimension and absolute observation filters still restrict evidence.',
      headline:
        'Sum of negative current-minus-own-peer-median margin/day gaps, capped at 28 remaining days, for well-below campaigns with at least seven days observed. A rate-gap prioritisation measure before refunds, not a loss estimate or forecast.',
      missing: 'Missing source records do not establish zero demand or complete instrumentation.',
      missingSalesReview:
        'Live campaigns with no sales records, at least seven view-record days and at least 500 views are flagged for investigation. Packages and margin remain unknown and are withheld from margin ranking and gap totals. Missing records do not verify zero bookings or ingestion completeness.',
      pcm: data?.sourceCapabilities?.pcm?.definition,
      ownerSalesforce: data?.sourceCapabilities?.ownerSalesforce?.definition,
      territory: data?.sourceCapabilities?.territory?.definition,
      margin: 'AUD sales margin before refunds, including booking items and surcharges.',
      weekly:
        'All eligible live and completed windows within the active dimension/date/market scope. weeklyExclusion defaults to empty (All Flash). Campaign labels and dates are in weeklyExclusionOptions. Choose at most 50 campaign exclusions. weeklyExclusion is a comma-separated list of exact campaign IDs from its filterSchema values. Send the complete desired list (empty string clears it); duplicates are removed. The selected IDs choose filtered instead of allIn for the weekly headline, chart and weekly CSV; it never excludes campaigns from peer scoring, the campaign queue or Previous Flash comparisons. Calendar weeks Monday–Sunday Sydney; last five full weeks versus seven immediately preceding full weeks. Recorded campaign-days count daily fact rows, not all scheduled days or contracted deals. The frequency/rate split is arithmetic, not causal.',
      trajectory:
        'Recorded cumulative values at the same launch age. Carrying recorded totals across interior missing records does not establish true zero activity. Peer membership changes with launch age; the band stops below half the original peer group or five peers, whichever is greater.',
      observation:
        'Launch filters and observation dates are separate. Observation dates intersect both campaign and peer facts. Status, search and campaign age restrict the current queue.',
      groupReview:
        'reviewBy and reviewGroup inspect members of a portfolio roll-up without changing the original peer comparison. They restrict the queue, selected campaign and exports; peer and weekly scopes remain the parent scope. The ordinary PCM and strategic-territory filters still rebenchmark within their selected scope.',
      reviewPriority:
        'The on-page campaign review queue contains comparable, below-quartile margin/day campaigns with at least seven observed days. Early and in-line campaigns remain in the All Campaigns view.',
      historicalAcceptance:
        'Historical workbook acceptance remains unresolved: Kempinski margin residual A$222.89 and aggregate view residual 4,349. Do not claim acceptance passed.',
      previousFlash:
        'Compare the current campaign with its immediately earlier same-property/type Flash run; exact same-offer matching is separately labelled when property identity is missing. Ambiguity, overlap or missing evidence is unavailable, never replaced with a convenient older run. Use first N launch-days on both sides, capped by each duration and current age. The same website market applies to both; absolute observation dates withhold this view. Current selection filters do not remove prior history. Portfolio metrics use only source-valid paired campaigns with at least seven aligned days, and each metric reports its own pair count. Percentage changes require a positive prior value. History begins at the snapshot historyStart; missing history does not establish the first-ever run. This is descriptive, not causal.',
    },
  };
}
